import { InlineMessageVariant } from '@ingka/inline-message';
import { MeasurementUtility } from '@inter-ikea-kompis/utilities';
import {
  CoreApi,
  CornerSolutionComponent,
  ErrorMessages,
  Range
} from '@inter-ikea-storageone/range';
import { isCornerFrameAllowingDoors } from '@inter-ikea-storageone/range-pax';
import { setHelpLinesLabelsVisible } from 'ducks/helpLines';
import { setRoomConfigurationActive } from 'ducks/layout';
import { selectGroup } from 'ducks/products';
import { setPresentRoomSize } from 'ducks/roomSizeHistory';
import { Dispatch } from 'react';
import { isMobile } from 'utils/platform';
import { getRange } from 'utils/queryUtils';
import { store } from 'ducks';
import { showSlidingDoorsInfoSheet } from 'ducks/sheets';
import { IntlShape } from 'react-intl';
import { IStorageOneTranslations } from 'contexts/serviceSettings';
import { rangeNameMap } from 'utils/range';

export interface ErrorMessage {
  title?: string;
  body: string;
  params?: any;
  type?: InlineMessageVariant;
  action?: { text: string; onClick(): void };
  showOnce?: boolean;
  translations?: IStorageOneTranslations;
  ssrIcon?: (...args: any[]) => any;
}

export const getToastMessage = (
  formatMessage: IntlShape['formatMessage'],
  coreApi: CoreApi,
  dispatch: Dispatch<any>,
  useMetric: boolean,
  message: ErrorMessages,
  translations: IStorageOneTranslations
): ErrorMessage | null => {
  switch (message) {
    case ErrorMessages.NoFramesInScene: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getNoFramesInSceneErrorMessage(dispatch);
    }
    case ErrorMessages.NoDoorsInScene: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getNoDoorsInSceneErrorMessage(dispatch);
    }
    case ErrorMessages.ItemWontFit: {
      return getItemWontFitErrorMessage();
    }
    case ErrorMessages.LegsWontFit: {
      return getLegsWontFitErrorMessage();
    }
    case ErrorMessages.PaxSlidingDoorOnCorner: {
      return getPaxSlidingDoorOnCornerErrorMessage();
    }
    case ErrorMessages.PaxDoorAppliedButNotFittingCorner: {
      return getPaxDoorAppliedButNotFittingCornerErrorMessage();
    }
    case ErrorMessages.PaxCornerFrameOpenCombination: {
      return getPaxCornerFrameOpenCombinationErrorMessage(dispatch, coreApi);
    }
    case ErrorMessages.PaxDoorHandlesOnCornerSolution: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getPaxDoorHandlesOnCornerSolutionErrorMessage();
    }
    case ErrorMessages.PaxDoorHandlesOnSlidingDoors: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getPaxDoorHandlesOnSlidingDoorErrorMessage();
    }
    case ErrorMessages.PaxDoorHandlesOnDoorsIntegratedHandles: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getPaxDoorHandlesOnDoorIntegratedHandlesErrorMessage();
    }
    case ErrorMessages.PaxDoorHandlesOnDoorsMixedIncludedIntegratedHandles: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getPaxDoorHandlesOnDoorsMixedIncludedIntegratedHandles();
    }
    case ErrorMessages.PaxSlidingDoorApplicableOnSomeFrames: {
      return getPaxSlidingDoorApplicableOnSomeFrames(dispatch);
    }
    case ErrorMessages.NoRoomForOuterLights: {
      return getNoRoomForOuterLightsErrorMessage();
    }
    case ErrorMessages.AddingEketToBestaFrames: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getAddingBestaEketButNoMatchingFramesErrorMessage();
    }
    case ErrorMessages.AddingBestaToEketFrames: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getAddingBestaEketButNoMatchingFramesErrorMessage();
    }
    case ErrorMessages.AddingToMixedBestaEketFrames: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here

      return getAddingMixedBestaEketFramesErrorMessage();
    }
    case ErrorMessages.AddingLightsToBestaSolidDoors: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getAddingLightsToBestaSolidDoorsErrorMessage(dispatch);
    }
    case ErrorMessages.AddingLightsToBestaMixedDoors: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getAddingLightsToBestaMixedDoorsErrorMessage();
    }
    case ErrorMessages.AddingBestaTopPanelNoMatchingFrames: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getBestaTopPanelNoMatchingFramesErrorMessage();
    }
    case ErrorMessages.PlatsaSlidingDoorsNotPossible: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getPlatsaSlidingDoorsNotPossibleErrorMessage(dispatch);
    }
    case ErrorMessages.PlatsaAddingDoorsToOpenFrames: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getPlatsaAddingDoorsToOpenFramesErrorMessage(dispatch);
    }
    case ErrorMessages.PlatsaAddingHandlesToOpenFrames: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getPlatsaAddingHandlesToOpenFramesErrorMessage(dispatch);
    }
    case ErrorMessages.PlatsaClothRailCollision: {
      return getPlatsaClothRailCollisionErrorMessage(
        dispatch,
        coreApi,
        useMetric,
        formatMessage,
        translations
      );
    }
    case ErrorMessages.SmastadAddingToFramesWithIncludedDoors: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getSmastadAddingToFramesWithIncludedDoorsErrorMessage(dispatch);
    }
    case ErrorMessages.SmastadAddingToMixedFramesWithIncludedDoors: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getSmastadAddingToMixedFramesWithIncludedDoorsErrorMessage();
    }
    case ErrorMessages.SmastadAddingToPullOutFrames: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getSmastadAddingToPullOutFramesErrorMessage(dispatch);
    }
    case ErrorMessages.SmastadAddingToMixedPullOutFrames: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getSmastadAddingToMixedPullOutFramesErrorMessage();
    }
    case ErrorMessages.IvarAddingDeskTo50DepthFrame: {
      return getIvarAddingDeskTo50DepthFrameErrorMessage(
        dispatch,
        coreApi,
        useMetric,
        formatMessage,
        translations
      );
    }
    case ErrorMessages.IvarAddingDrawerWontFit: {
      return getIvarAddingDrawerWontFitErrorMessage(
        dispatch,
        coreApi,
        useMetric,
        formatMessage,
        translations
      );
    }
    case ErrorMessages.BrorAddingWallSystemInteriorToRegularFrame: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getBrorAddingWallSystemInteriorToRegularFrameErrorMessage();
    }
    case ErrorMessages.BrorAddingRegularInteriorToWallSystemFrame: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getBrorAddingRegularInteriorToWallSystemFrameErrorMessage();
    }
    case ErrorMessages.BrorNoMatchingFramesForCabinets: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getBrorNoMatchingFramesForCabinetsErrorMessage(
        dispatch,
        coreApi,
        useMetric,
        formatMessage,
        translations
      );
    }
    case ErrorMessages.BrorNoMatchingFramesForDrawers: {
      if (!isMobile()) return null; // Desktop dispalys as widget, no need to trigger toast here
      return getBrorNoMatchingFramesForDrawersErrorMessage(
        dispatch,
        coreApi,
        useMetric,
        formatMessage,
        translations
      );
    }
    case ErrorMessages.BillyExtensionWontFit: {
      const initialDimensions = coreApi?.kvadratRoom?.getDimensions();
      return getBillyExtensionWontFitErrorMessage(dispatch, initialDimensions);
    }
    case ErrorMessages.BillyCornerFrameNoDoors: {
      return getBillyCornerFrameNoDoorsErrorMessage();
    }
    case ErrorMessages.HandlesTooBigForDoor: {
      return getHandlesTooBigForDoorErrorMessage();
    }
    case ErrorMessages.BillyExtensionMaxTwo: {
      return getBillyExtensionMaxTwoErrorMessage();
    }
    case ErrorMessages.BillyExtensionSmallFrame: {
      return getBillyExtensionSmallFrameErrorMessage();
    }
    case ErrorMessages.BillyMoveFrameToCornerDeleteExtAndDoor: {
      return getBillyMoveFrameToCornerDeleteExtAndDoor(coreApi);
    }
    case ErrorMessages.BillyInteriorCollidesWithDoor: {
      return getBillyInteriorCollidesWithDoor();
    }
    case ErrorMessages.BestaTopPanelsNotAddedToSomeFramesMessage:
      return {
        body: 'storageone.topPanelPlacementIssueToastMessage'
      };
    case ErrorMessages.BestaTopPanelsAbove150Message: {
      const heightLimit = 1500;
      const height = useMetric
        ? heightLimit / 10
        : MeasurementUtility.metricToImperial(translations.kompis, heightLimit);
      const unit = formatMessage({ id: useMetric ? 'storageone.cm' : 'storageone.inches' });

      return {
        body: 'storageone.addTopPanelHeighRestrictionToastMessage',
        params: { height, unit },
        showOnce: true
      };
    }
    case ErrorMessages.LadmakareInteriorOnWideFramesOnly: {
      const widthLimit = 800;
      const width = useMetric
        ? widthLimit / 10
        : MeasurementUtility.metricToImperial(translations.kompis, widthLimit);
      const unit = formatMessage({ id: useMetric ? 'storageone.cm' : 'storageone.inches' });

      return {
        body: 'storageone.errorMessageLadmakareInteriorOnWideFramesOnly',
        params: { width, unit }
      };
    }
    case ErrorMessages.SmastadExteriorAccessoryOnDoorsOnly: {
      return { body: 'storageone.errorMessageSmastadExteriorAccessoryOnDoorsOnlyBody' };
    }
    case ErrorMessages.LadmakareCabinetBlockedByOtherCabinet: {
      return { body: 'storageone.errorMessageLadmakareCabinetBlockedByOtherCabinet' };
    }
    case ErrorMessages.BoaxelClothesRailRequiresShelf: {
      return { body: 'storageone.errorMessageBoaxelClothesRailRequiresShelf' };
    }
    default:
      return getDefaultErrorMessage(message);
  }
};

export const getNoFramesInSceneErrorMessage = (dispatch: Dispatch<any>): ErrorMessage => {
  const getTitle = () => {
    if (getRange() === 'billy') {
      return 'storageone.errorMessageNoBookcasesInSceneTitle';
    } else if (getRange() === 'kallax') {
      return 'storageone.errorMessageNoShelvingUnitsInSceneTitle';
    } else if (
      getRange() === 'ivar' ||
      getRange() === 'bror' ||
      getRange() === 'boaxel' ||
      getRange() === 'elvarli'
    ) {
      return 'storageone.errorMessageNoSectionsInSceneTitle';
    } else {
      return 'storageone.errorMessageNoFramesInSceneTitle';
    }
  };
  const getBody = () => {
    if (getRange() === 'billy') {
      return 'storageone.errorMessageNoBookcasesInSceneBody';
    } else if (getRange() === 'kallax') {
      return 'storageone.errorMessageNoShelvingUnitsInSceneBody';
    } else if (
      getRange() === 'ivar' ||
      getRange() === 'bror' ||
      getRange() === 'boaxel' ||
      getRange() === 'elvarli'
    ) {
      return 'storageone.errorMessageNoSectionsInSceneBody';
    } else {
      return 'storageone.errorMessageNoFramesInSceneBody';
    }
  };
  const getActionText = () => {
    if (getRange() === 'billy') {
      return 'storageone.errorMessageNoBookcasesInScenePrimaryAction';
    } else if (getRange() === 'kallax') {
      return 'storageone.errorMessageNoShelvingUnitsInScenePrimaryAction';
    } else if (
      getRange() === 'ivar' ||
      getRange() === 'bror' ||
      getRange() === 'boaxel' ||
      getRange() === 'elvarli'
    ) {
      return 'storageone.errorMessageNoSectionsInScenePrimaryAction';
    } else {
      return 'storageone.errorMessageNoFramesInScenePrimaryAction';
    }
  };

  return {
    title: getTitle(),
    body: getBody(),
    type: 'negative' as InlineMessageVariant,
    action: {
      onClick: () => {
        if (
          getRange() === 'ivar' ||
          getRange() === 'bror' ||
          getRange() === 'boaxel' ||
          getRange() === 'elvarli'
        ) {
          const selectedRange = store.getState().range.selectedRange;
          const sectionsKey = selectedRange !== 'bror-wall-mounted' ? 'sections' : 'wall-sections';
          dispatch(selectGroup(sectionsKey));
        } else if (
          getRange() == 'eket' ||
          (getRange() === 'besta' && store.getState().range.selectedRange === 'eket')
        ) {
          dispatch(selectGroup('frames-eket'));
        } else {
          dispatch(selectGroup('frames'));
        }
      },
      text: getActionText()
    }
  };
};

export const getNoDoorsInSceneErrorMessage = (dispatch: Dispatch<any>): ErrorMessage => {
  const doorsOrDrawers =
    getRange() === 'besta' || getRange() === 'platsa' || getRange() === 'smastad';

  return {
    title: doorsOrDrawers
      ? 'storageone.errorMessageNoDoorsOrDrawersInSceneTitle'
      : 'storageone.errorMessageNoDoorsInSceneTitle',
    body: doorsOrDrawers
      ? 'storageone.errorMessageNoDoorsOrDrawersInSceneBody'
      : 'storageone.errorMessageNoDoorsInSceneBody',
    type: 'negative' as InlineMessageVariant,
    action: {
      onClick: () => {
        dispatch(selectGroup(doorsOrDrawers ? 'fronts' : 'doors'));
      },
      text: doorsOrDrawers
        ? 'storageone.errorMessageNoDoorsOrDrawersInScenePrimaryAction'
        : 'storageone.errorMessageNoDoorsInScenePrimaryAction'
    }
  };
};

export const getItemWontFitErrorMessage = (): ErrorMessage => {
  if (getRange() === 'billy') {
    return {
      body: 'storageone.errorMessageBillyItemWontFitBody'
    };
  } else if (getRange() === 'ivar' || getRange() === 'bror' || getRange() === 'elvarli') {
    return {
      body: 'storageone.errorMessageIvarBrorItemWontFitBody'
    };
  }
  return {
    body: 'storageone.errorMessageItemWontFitBody'
  };
};

export const getLegsWontFitErrorMessage = (): ErrorMessage => {
  return {
    body: 'storageone.errorMessageLegsWontFitBody'
  };
};

export const getNoRoomForOuterLightsErrorMessage = (): ErrorMessage => {
  return {
    body: 'storageone.errorMessageNoRoomForOuterLightsBody'
  };
};

export const getPaxSlidingDoorOnCornerErrorMessage = (): ErrorMessage => {
  return {
    body: 'storageone.errorMessagePaxSlidingDoorOnCornerBody'
  };
};

export const getPaxDoorAppliedButNotFittingCornerErrorMessage = (): ErrorMessage => {
  return {
    body: 'storageone.errorMessagePaxDoorAppliedButNotFittingCornerBody'
  };
};

export const getPaxCornerFrameOpenCombinationErrorMessage = (
  dispatch: Dispatch<any>,
  coreApi: CoreApi
): ErrorMessage => {
  return {
    title: 'storageone.errorMessagePaxCornerFrameOpenCombinationTitle',
    body: 'storageone.errorMessagePaxCornerFrameOpenCombinationBody',
    action: {
      onClick: () => {
        const cornerFramesInScene = coreApi.entities.getByComponent(CornerSolutionComponent.id);
        const openCornerFrameInScene = cornerFramesInScene.find(
          c => !isCornerFrameAllowingDoors(coreApi, c)
        );
        if (openCornerFrameInScene) {
          coreApi.entities.select(openCornerFrameInScene);
        }
      },
      text: 'storageone.errorMessagePaxCornerFrameOpenCombinationPrimaryAction'
    }
  };
};

export const getPaxDoorHandlesOnCornerSolutionErrorMessage = (): ErrorMessage => {
  return {
    title: 'storageone.errorMessagePaxDoorHandlesOnCornerSolutionTitle',
    body: 'storageone.errorMessagePaxDoorHandlesOnCornerSolutionBody',
    type: 'informative'
  };
};

export const getPaxDoorHandlesOnSlidingDoorErrorMessage = (): ErrorMessage => {
  return {
    title: 'storageone.errorMessagePaxDoorHandlesOnSlidingDoorsTitle',
    body: 'storageone.errorMessagePaxDoorHandlesOnSlidingDoorsBody'
  };
};

export const getPaxDoorHandlesOnDoorIntegratedHandlesErrorMessage = (): ErrorMessage => {
  return {
    title: 'storageone.errorMessagePaxDoorHandlesOnDoorsIntegratedHandlesTitle',
    body: 'storageone.errorMessagePaxDoorHandlesOnDoorsIntegratedHandlesBody'
  };
};
export const getPaxDoorHandlesOnDoorsMixedIncludedIntegratedHandles = (): ErrorMessage => {
  return {
    title: 'storageone.errorMessagePaxDoorHandlesOnDoorsMixedSlidingIntegratedHandlesTitle',
    body: 'storageone.errorMessagePaxDoorHandlesOnDoorsMixedSlidingIntegratedHandlesBody',
    type: 'informative'
  };
};
export const getPaxSlidingDoorApplicableOnSomeFrames = (dispatch: Dispatch<any>): ErrorMessage => {
  return {
    body: 'storageone.errorMessagePaxSlidingDoorApplicableOnSomeFramesBody',
    action: {
      onClick: () => {
        dispatch(showSlidingDoorsInfoSheet());
      },
      text: 'storageone.errorMessagePaxSlidingDoorApplicableOnSomeFramesPrimaryAction'
    }
  };
};

export const getAddingBestaEketButNoMatchingFramesErrorMessage = (): ErrorMessage => {
  const range = store.getState().range.selectedRange as Range;
  const other_range = rangeNameMap[range];
  const current = range === 'besta' ? 'eket' : 'besta';
  const current_range = rangeNameMap[current];
  return {
    title: other_range,
    body: 'storageone.errorMessageBestaEketAddingButNoMatchingFramesBody',
    params: { current_range, other_range }
  };
};

export const getAddingMixedBestaEketFramesErrorMessage = (): ErrorMessage => {
  const currentRange = store.getState().range.selectedRange as Range;
  const range = rangeNameMap[currentRange];
  return {
    title: range,
    body: 'storageone.errorMessageBestaEketAddingToMixedFramesBody',
    params: { range: range },
    type: 'informative'
  };
};

export const getAddingLightsToBestaSolidDoorsErrorMessage = (
  dispatch: Dispatch<any>
): ErrorMessage => {
  return {
    title: 'storageone.errorMessageAddingLightsToBestaSolidDoorsTitle',
    body: 'storageone.errorMessageAddingLightsToBestaSolidDoorsBody',
    action: {
      onClick: () => {
        dispatch(selectGroup('fronts'));
      },
      text: 'storageone.errorMessageAddingLightsToBestaSolidDoorsPrimaryAction'
    }
  };
};

export const getAddingLightsToBestaMixedDoorsErrorMessage = (): ErrorMessage => {
  return {
    title: 'storageone.errorMessageAddingLightsToBestaMixedDoorsTitle',
    body: 'storageone.errorMessageAddingLightsToBestaMixedDoorsBody',
    type: 'informative'
  };
};

export const getPlatsaSlidingDoorsNotPossibleErrorMessage = (
  dispatch: Dispatch<any>
): ErrorMessage => {
  return {
    title: 'storageone.errorMessagePlatsaSlidingDoorsNotPossibleTitle',
    body: 'storageone.errorMessagePlatsaSlidingDoorsNotPossibleBody',
    action: {
      onClick: () => {
        dispatch(selectGroup('frames'));
      },
      text: 'storageone.errorMessagePlatsaSlidingDoorsNotPossiblePrimaryAction'
    }
  };
};

export const getPlatsaAddingDoorsToOpenFramesErrorMessage = (
  dispatch: Dispatch<any>
): ErrorMessage => {
  return {
    title: 'storageone.errorMessagePlatsaAddingDoorsToOpenFramesTitle',
    body: 'storageone.errorMessagePlatsaAddingDoorsToOpenFramesBody',
    action: {
      onClick: () => {
        dispatch(selectGroup('frames'));
      },
      text: 'storageone.errorMessagePlatsaAddingDoorsToOpenFramesPrimaryAction'
    }
  };
};

export const getPlatsaAddingHandlesToOpenFramesErrorMessage = (
  dispatch: Dispatch<any>
): ErrorMessage => {
  return {
    title: 'storageone.errorMessagePlatsaAddingHandlesToOpenFramesTitle',
    body: 'storageone.errorMessagePlatsaAddingHandlesToOpenFramesBody',
    action: {
      onClick: () => {
        dispatch(selectGroup('frames'));
      },
      text: 'storageone.errorMessagePlatsaAddingHandlesToOpenFramesPrimaryAction'
    }
  };
};

export const getSmastadAddingToFramesWithIncludedDoorsErrorMessage = (
  dispatch: Dispatch<any>
): ErrorMessage => {
  return {
    title: 'storageone.errorMessageSmastadAddingToFramesWithIncludedDoorsTitle',
    body: 'storageone.errorMessageSmastadAddingToFramesWithIncludedDoorsBody',
    action: {
      onClick: () => {
        dispatch(selectGroup('frames'));
      },
      text: 'storageone.errorMessageSmastadAddingToFramesWithIncludedDoorsPrimaryAction'
    }
  };
};

export const getSmastadAddingToMixedFramesWithIncludedDoorsErrorMessage = (): ErrorMessage => {
  return {
    title: 'storageone.errorMessageSmastadAddingToMixedFramesWithIncludedDoorsTitle',
    body: 'storageone.errorMessageSmastadAddingToMixedFramesWithIncludedDoorsBody',
    type: 'informative'
  };
};

export const getSmastadAddingToPullOutFramesErrorMessage = (
  dispatch: Dispatch<any>
): ErrorMessage => {
  return {
    title: 'storageone.errorMessageSmastadAddingToPullOutFramesTitle',
    body: 'storageone.errorMessageSmastadAddingToPullOutFramesBody',
    action: {
      onClick: () => {
        dispatch(selectGroup('frames'));
      },
      text: 'storageone.errorMessageSmastadAddingToPullOutFramesPrimaryAction'
    }
  };
};

export const getSmastadAddingToMixedPullOutFramesErrorMessage = (): ErrorMessage => {
  return {
    title: 'storageone.errorMessageSmastadAddingToMixedPullOutFramesTitle',
    body: 'storageone.errorMessageSmastadAddingToMixedPullOutFramesBody',
    type: 'informative'
  };
};

export const getIvarAddingDeskTo50DepthFrameErrorMessage = (
  dispatch: Dispatch<any>,
  coreApi: CoreApi,
  useMetric: boolean,
  formatMessage: IntlShape['formatMessage'],
  translations: IStorageOneTranslations
): ErrorMessage => {
  const widthInMm = 890;
  const depthInMm = 890;
  const width = useMetric
    ? widthInMm / 10
    : MeasurementUtility.metricToImperial(translations.kompis, widthInMm);
  const depth = useMetric
    ? depthInMm / 10
    : MeasurementUtility.metricToImperial(translations.kompis, depthInMm);
  const unit = formatMessage({ id: useMetric ? 'storageone.cm' : 'storageone.inches' });

  return {
    body: 'storageone.errorMessageIvarAddingDeskTo50DepthFrameBody',
    params: { unit, width, depth }
  };
};

export const getIvarAddingDrawerWontFitErrorMessage = (
  dispatch: Dispatch<any>,
  coreApi: CoreApi,
  useMetric: boolean,
  formatMessage: IntlShape['formatMessage'],
  translations: IStorageOneTranslations
): ErrorMessage => {
  const widthInMm = 890;
  const width = useMetric
    ? widthInMm / 10
    : MeasurementUtility.metricToImperial(translations.kompis, widthInMm);
  const unit = formatMessage({ id: useMetric ? 'storageone.cm' : 'storageone.inches' });

  return {
    body: 'storageone.errorMessageIvarAddingDrawerWontFitBody',
    params: { unit, width }
  };
};

export const getBrorAddingWallSystemInteriorToRegularFrameErrorMessage = (): ErrorMessage => {
  return {
    title: 'storageone.errorMessageBrorAddingWallSystemInteriorToRegularFrameTitle',
    body: 'storageone.errorMessageBrorAddingWallSystemInteriorToRegularFrameBody'
  };
};

export const getBrorAddingRegularInteriorToWallSystemFrameErrorMessage = (): ErrorMessage => {
  return {
    title: 'storageone.errorMessageBrorAddingRegularInteriorToWallSystemFrameTitle',
    body: 'storageone.errorMessageBrorAddingRegularInteriorToWallSystemFrameBody'
  };
};

export const getBillyExtensionWontFitErrorMessage = (
  dispatch: Dispatch<any>,
  initialDimensions: { x: number; y: number; z: number } | undefined
): ErrorMessage => {
  return {
    body: 'storageone.errorMessageBillyFrameExtensionWontFitBody',
    action: {
      onClick: () => {
        if (initialDimensions) {
          dispatch(setPresentRoomSize(initialDimensions));
        }

        dispatch(setRoomConfigurationActive(true)); // TODO: not sure if needed?
        dispatch(setHelpLinesLabelsVisible(true)); // TODO: not sure if needed?
      },
      text: 'storageone.errorMessageBillyFrameExtensionWontFitPrimaryAction'
    }
  };
};

export const getDefaultErrorMessage = (messageKey: string): ErrorMessage => {
  return {
    body: `storageone.${messageKey}`
  };
};

export const getBillyCornerFrameNoDoorsErrorMessage = (): ErrorMessage => {
  return {
    body: 'storageone.errorMessageBillyCornerFrameNoDoorsBody',
    showOnce: true
  };
};

export const getHandlesTooBigForDoorErrorMessage = (): ErrorMessage => {
  return {
    body: 'storageone.errorMessageHandlesTooBigForDoorBody'
  };
};

export const getBillyExtensionMaxTwoErrorMessage = (): ErrorMessage => {
  return {
    body: 'storageone.errorMessageBillyFrameExtensionMaxTwo'
  };
};

export const getBillyExtensionSmallFrameErrorMessage = (): ErrorMessage => {
  return {
    body: 'storageone.errorMessageBillyFrameExtensionOnSmallFrame',
    showOnce: true
  };
};

export const getBillyMoveFrameToCornerDeleteExtAndDoor = (coreApi: CoreApi): ErrorMessage => {
  return {
    body: 'storageone.errorMessageBillyMoveFrameToCornerDeleteExtAndDoorBody',
    action: {
      onClick: () => {
        const onUndo = () => {
          coreApi.history.undo();
        };
        onUndo();
      },
      text: 'storageone.errorMessageBillyMoveFrameToCornerDeleteExtAndDoorPrimaryAction'
    }
  };
};

export const getBillyInteriorCollidesWithDoor = (): ErrorMessage => {
  return {
    body: 'storageone.errorMessageBillyInteriorCollidesWithDoorBody'
  };
};

export const getBestaTopPanelNoMatchingFramesErrorMessage = (): ErrorMessage => {
  return {
    title: 'storageone.errorMessageAddingBestaTopPanelNoMatchingFramesErrorMessageTitle',
    body: 'storageone.errorMessageAddingBestaTopPanelNoMatchingFramesErrorMessageBody'
  };
};

export const getPlatsaClothRailCollisionErrorMessage = (
  dispatch: Dispatch<any>,
  coreApi: CoreApi,
  useMetric: boolean,
  formatMessage: IntlShape['formatMessage'],
  translations: IStorageOneTranslations
): ErrorMessage => {
  const widthInMm = 400;
  const width = useMetric
    ? widthInMm / 10
    : MeasurementUtility.metricToImperial(translations.kompis, widthInMm);
  const unit = formatMessage({ id: useMetric ? 'storageone.cm' : 'storageone.inches' });

  return {
    body: 'storageone.errorMessagePlatsaClothRailCollisionBody',
    params: { unit, width }
  };
};

export const getBrorNoMatchingFramesForCabinetsErrorMessage = (
  dispatch: Dispatch<any>,
  coreApi: CoreApi,
  useMetric: boolean,
  formatMessage: IntlShape['formatMessage'],
  translations: IStorageOneTranslations
): ErrorMessage => {
  const depthInMm = 400;
  const widthInMm = 850;
  const width = useMetric
    ? widthInMm / 10
    : MeasurementUtility.metricToImperial(translations.kompis, widthInMm);
  const depth = useMetric
    ? depthInMm / 10
    : MeasurementUtility.metricToImperial(translations.kompis, widthInMm);
  const unit = formatMessage({ id: useMetric ? 'storageone.cm' : 'storageone.inches' });

  return {
    title: 'storageone.errorMessageBrorNoMatchingSectionsForCabinetTitle',
    body: 'storageone.errorMessageBrorNoMatchingSectionsForCabinetBody',
    params: { unit, width, depth },
    type: 'informative'
  };
};

export const getBrorNoMatchingFramesForDrawersErrorMessage = (
  dispatch: Dispatch<any>,
  coreApi: CoreApi,
  useMetric: boolean,
  formatMessage: IntlShape['formatMessage'],
  translations: IStorageOneTranslations
): ErrorMessage => {
  const widthInMm = 850;
  const width = useMetric
    ? widthInMm / 10
    : MeasurementUtility.metricToImperial(translations.kompis, widthInMm);
  const unit = formatMessage({ id: useMetric ? 'storageone.cm' : 'storageone.inches' });
  return {
    title: 'storageone.errorMessageBrorNoMatchingSectionsForDrawerTitle',
    body: 'storageone.errorMessageBrorNoMatchingSectionsForDrawerBody',
    params: { unit, width },
    type: 'negative' as InlineMessageVariant,
    action: {
      onClick: () => {
        dispatch(selectGroup('sections'));
      },
      text: 'storageone.errorMessageBrorNoMatchingSectionsForDrawerPrimaryAction'
    }
  };
};

export const getNoFramesForColorDepthCombination = (): ErrorMessage => {
  return {
    title: 'storageone.errorMessageNoFramesForColorDepthCombinationTitle',
    body: 'storageone.errorMessageNoFramesForColorDepthCombinationBody'
  };
};
